<script setup lang="ts">
import { brandRef } from "../brand-state";
/**
 * Where every assistant stands, and the periodic review (ADR 0066) — the
 * Approvals page.
 *
 * Start a round and every sign-off given before today is due again: each
 * data owner and AI Board member finds theirs under My sign-offs. The card
 * shows how far the round has come, by who has to act. Close it after the
 * meeting with who took part and what was decided; what is still open is
 * kept in the report as open — accepting what is safe now and leaving the
 * rest is a legitimate outcome.
 *
 * The review pack is the one-page Markdown for the meeting: every assistant
 * with its three steps, the agreements, and who signs.
 */
import { vWrite } from "../admin-read-only";
import { computed, onMounted, ref } from "vue";
import {
  AdminError,
  type ReviewItem,
  type ReviewRoundsResponse,
  closeReviewRound,
  getReviewMarkdown,
  getReviewRounds,
  startReviewRound,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { saveBlob } from "../utils/download";
import { adminLink } from "../utils/vocabulary";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ devUser: string }>();
const toast = useToast();
const { confirm } = useConfirm();

const data = ref<ReviewRoundsResponse | null>(null);
const busy = ref("");

function fail(e: unknown, what: string): void {
  toast.error(what, e instanceof AdminError ? e.message : String(e));
}

async function load(): Promise<void> {
  try {
    data.value = await getReviewRounds(props.devUser);
  } catch (e) {
    fail(e, "Could not load the review round");
  }
}
onMounted(load);

const today = new Date().toISOString().slice(0, 10);
const dueOn = ref("");
const heldOn = ref(today);
const attendees = ref("");
const notes = ref("");

async function start(): Promise<void> {
  const ok = await confirm({
    title: "Start a review round?",
    message:
      "From now, every confirmation and acceptance given before today is due " +
      "again. Data owners and the AI Board see theirs under My sign-offs. " +
      "Nothing stops working meanwhile.",
    confirmLabel: "Start round",
  });
  if (!ok) return;
  busy.value = "start";
  try {
    data.value = await startReviewRound(
      { due_on: dueOn.value || undefined },
      props.devUser,
    );
    toast.success("Review round started");
  } catch (e) {
    fail(e, "Could not start the round");
  } finally {
    busy.value = "";
  }
}

async function close(): Promise<void> {
  const r = data.value?.open;
  if (!r) return;
  const left = (data.value?.pack.total ?? 0) - (data.value?.pack.done ?? 0);
  const ok = await confirm({
    title: `Close ${r.title}?`,
    message:
      (left
        ? `${left} item(s) are still open; the report keeps them as open. `
        : "Everything is done. ") +
      "The report is saved as it is now and can be downloaded afterwards.",
    confirmLabel: "Close round",
  });
  if (!ok) return;
  busy.value = "close";
  try {
    data.value = await closeReviewRound(
      r.id,
      {
        held_on: heldOn.value,
        attendees: attendees.value.trim(),
        notes: notes.value.trim(),
      },
      props.devUser,
    );
    attendees.value = "";
    notes.value = "";
    toast.success("Round closed — the report is saved");
  } catch (e) {
    fail(e, "Could not close the round");
  } finally {
    busy.value = "";
  }
}

async function download(id: number | null): Promise<void> {
  busy.value = `md:${id ?? "pack"}`;
  try {
    const blob = await getReviewMarkdown(id, props.devUser);
    saveBlob(
      blob,
      id === null ? `review-pack-${today}.md` : `review-round-${id}.md`,
    );
  } catch (e) {
    fail(e, "Could not download");
  } finally {
    busy.value = "";
  }
}

/** Open items grouped by who acts — the same people My sign-offs serves. */
const openByWho = computed(() => {
  const m = new Map<string, ReviewItem[]>();
  for (const i of data.value?.pack.items ?? []) {
    if (i.done) continue;
    const who =
      i.kind === "accept"
        ? `AI Board (${i.who})`
        : i.kind === "confirm"
          ? `Data owner: ${i.who}`
          : i.who;
    m.set(who, [...(m.get(who) ?? []), i]);
  }
  return [...m.entries()];
});

function linkOf(i: ReviewItem): string {
  if (i.kind === "agreement")
    return adminLink("data-sources", { source: i.source_key ?? "" });
  return adminLink("assistants", {
    assistant: i.slug ?? "",
    section: i.kind === "accept" ? "compliance" : "access",
  });
}

const pct = computed(() => {
  const p = data.value?.pack;
  return p && p.total ? Math.round((100 * p.done) / p.total) : 0;
});
const lastClosed = computed(() =>
  (data.value?.rounds ?? []).find((r) => r.closed_at),
);
</script>

<template>
  <section v-if="data" class="card review">
    <div class="review__head">
      <h3>Where every assistant stands</h3>
      <span class="grow" />
      <button
        class="btn"
        :disabled="busy === 'md:pack'"
        @click="download(null)"
      >
        {{ busy === "md:pack" ? "Preparing…" : "Download review pack" }}
      </button>
    </div>
    <p class="legend small">
      <span><b>Sign</b> — an agreement for a data source, once per version</span>
      <span><b>Confirm</b> — a data owner says who may see their data</span>
      <span><b>Test</b> — {{ brandRef.productName }} checks it, by itself</span>
      <span><b>Accept</b> — the AI Board lets the assistant be used</span>
    </p>

    <div class="table-wrap">
      <table class="matrix">
        <thead>
          <tr>
            <th>Assistant</th>
            <th>Used by</th>
            <th class="c">1 Confirm</th>
            <th class="c">2 Test</th>
            <th class="c">3 Accept</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="a in data.pack.assistants" :key="a.slug">
            <td>
              {{ a.name
              }}<span v-if="a.maturity === 'pilot'" class="muted"> · pilot</span>
            </td>
            <td class="muted">{{ a.audience.join(", ") }}</td>
            <td v-for="st in a.steps" :key="st.key" class="c">
              <a
                :href="
                  adminLink('assistants', {
                    assistant: a.slug,
                    section: st.key === 'accepted' ? 'compliance' : 'access',
                  })
                "
                :class="['mark', `mark--${st.status}`]"
                :title="st.detail || st.label"
                >{{ { ok: "✓", todo: "✗", na: "–" }[st.status] }}</a
              >
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="small muted">
      ✓ done · ✗ open (hover for what is left) · – not needed. Agreements:
      {{ data.pack.sources.filter((x) => x.agreement_state === "signed").length }}
      of {{ data.pack.sources.length }} signed.
    </p>

    <template v-if="data.open">
      <div class="round">
        <StatusChip tone="info" dot>round open</StatusChip>
        <span class="small">
          <strong>{{ data.open.title }}</strong
          ><template v-if="data.open.due_on">, meeting {{ data.open.due_on }}</template
          > — {{ data.pack.done }} of {{ data.pack.total }} done
        </span>
      </div>
      <div
        class="bar"
        role="progressbar"
        :aria-valuenow="pct"
        aria-valuemin="0"
        aria-valuemax="100"
      >
        <span :style="{ width: `${pct}%` }" />
      </div>
      <details class="who">
        <summary>Open items by person</summary>
        <div v-for="[who, items] in openByWho" :key="who">
          <h4>{{ who }} · {{ items.length }}</h4>
          <ul>
            <li
              v-for="i in items"
              :key="`${i.kind}:${i.slug}:${i.source_key}`"
            >
              <a :href="linkOf(i)">{{ i.what }}</a>
            </li>
          </ul>
        </div>
      </details>
      <details class="who">
        <summary>Close the round after the meeting</summary>
        <form v-write class="close" @submit.prevent="close">
          <div class="grid">
            <label class="field">
              Held on
              <input v-model="heldOn" type="date" required />
            </label>
            <label class="field">
              Who took part
              <input
                v-model="attendees"
                required
                maxlength="1000"
                placeholder="Carina Rich Thomsen, Lars Lisberg, Elfar Thorarinsson"
              />
            </label>
          </div>
          <label class="field">
            Decisions and notes
            <textarea
              v-model="notes"
              rows="3"
              maxlength="4000"
              placeholder="What was accepted, what is left and why"
            />
          </label>
          <button class="btn btn--primary" :disabled="busy === 'close'">
            {{ busy === "close" ? "Closing…" : "Close round and save the report" }}
          </button>
        </form>
      </details>
    </template>

    <div v-else class="start">
      <span class="small muted">
        Once a year: a review round makes every sign-off due again.
        <template v-if="lastClosed">
          Last: {{ lastClosed.title }}, {{ lastClosed.held_on }}.</template
        >
      </span>
      <span class="grow" />
      <label class="field inline">
        Meeting
        <input v-model="dueOn" type="date" />
      </label>
      <button
        class="btn btn--primary"
        :disabled="busy === 'start'"
        v-write
        @click="start"
      >
        {{ busy === "start" ? "Starting…" : "Start a review round" }}
      </button>
    </div>

    <details v-if="data.rounds.some((r) => r.closed_at)" class="small past">
      <summary>Past rounds</summary>
      <ul>
        <li v-for="r in data.rounds.filter((x) => x.closed_at)" :key="r.id">
          {{ r.title }} — held {{ r.held_on }}, {{ r.attendees }}
          <button
            class="link"
            :disabled="busy === `md:${r.id}`"
            @click="download(r.id)"
          >
            report
          </button>
        </li>
      </ul>
    </details>
  </section>
</template>

<style scoped>
.review {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.review__head {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}
.review__head h3 {
  margin: 0;
}
.grow {
  flex: 1;
}
.small {
  font-size: 0.85rem;
}
.muted {
  color: var(--color-muted);
}
p {
  margin: 0;
}
.bar {
  height: 0.5rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--color-ink) 8%, transparent);
  overflow: hidden;
}
.bar span {
  display: block;
  height: 100%;
  background: var(--color-success);
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem 1.2rem;
  color: var(--color-muted);
}
.legend b {
  color: var(--color-ink);
}
.table-wrap {
  overflow-x: auto;
  min-width: 0;
}
.matrix {
  border-collapse: collapse;
  width: 100%;
  font-size: 0.88rem;
}
.matrix th,
.matrix td {
  padding: 0.35rem 0.6rem;
  border-bottom: 1px solid var(--color-border);
  text-align: left;
  white-space: nowrap;
}
.matrix td:nth-child(2) {
  white-space: normal;
}
.matrix th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-muted);
}
.matrix .c {
  text-align: center;
}
.mark {
  display: inline-flex;
  width: 1.6rem;
  height: 1.6rem;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 700;
  text-decoration: none;
}
.mark--ok {
  color: var(--color-surface);
  background: var(--color-success);
}
.mark--todo {
  color: var(--color-error);
  border: 1.5px solid var(--color-error);
}
.mark--na {
  color: var(--color-muted);
}
.round {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}
.field.inline {
  flex-direction: row;
  align-items: center;
  gap: 0.4rem;
}
.who h4 {
  margin: 0.4rem 0 0.2rem;
  font-size: 0.88rem;
}
.who summary {
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0.15rem 0;
}
.who ul {
  margin: 0.2rem 0 0.4rem;
}
.who ul,
.past ul {
  margin: 0;
  padding-left: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  font-size: 0.85rem;
}
.close,
.start {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  border-top: 1px solid var(--color-border);
  padding-top: var(--space-2);
}
.start {
  flex-direction: row;
  align-items: flex-end;
  flex-wrap: wrap;
}
.close h4 {
  margin: 0;
  font-size: 0.9rem;
}
.close .btn {
  align-self: flex-start;
}
.grid {
  display: grid;
  grid-template-columns: minmax(9rem, 12rem) 1fr;
  gap: var(--space-2);
}
@media (max-width: 640px) {
  .grid {
    grid-template-columns: 1fr;
  }
}
.field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.85rem;
  color: var(--color-muted);
  min-width: 0;
}
.field input,
.field textarea {
  font: inherit;
  font-size: 0.92rem;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-ink);
  min-width: 0;
}
.link {
  background: none;
  border: 0;
  padding: 0;
  color: var(--color-accent);
  cursor: pointer;
  font: inherit;
}
.past summary {
  cursor: pointer;
  color: var(--color-muted);
}
</style>
